import { Lightning } from '@lightningjs/sdk'

import { getColor, grey50, grey700, grey960, transparent } from '../../constants/colors'
import { UniNeueBold } from '../../constants/fonts'
import { getAsset } from '../../utils/image'
import SpeechComp from '../speechComp/speechComp'

class PlayerButton extends SpeechComp {
  protected _offsetX = 0
  protected _offsetY = 0

  static _template() {
    return {
      w: 84,
      h: 144,
      flex: { direction: 'column', alignItems: 'center', justifyContent: 'flex-end' },
      collision: true,

      Label: {
        shader: null,
        text: {
          text: '',
          fontSize: 32,
          fontFace: UniNeueBold,
        },
        flexItem: { marginBottom: 10 },
      },
      Button: {
        w: 84,
        h: 84,
        rect: true,
        color: transparent,
        alpha: 1,
        flex: { direction: 'column', alignItems: 'center', justifyContent: 'center' },
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: 41,
        },
        Image: {
          w: 36,
          h: 36,
          shader: null,
        },
      },
    }
  }

  set label(v: string) {
    this.tag('Label').patch({ text: v || '' })
  }

  set image(path) {
    this.tag('Image').patch({
      texture: Lightning.Tools.getSvgTexture(getAsset(path), 36, 36),
      flexItem: { marginTop: this._offsetY, marginLeft: this._offsetX },
    })
  }

  set offsetX(x: number) {
    this._offsetX = x
  }

  set offsetY(y: number) {
    this._offsetY = y
  }

  get speechText(): string {
    return this.label
  }

  _focus() {
    this.focusState()
    this.speak()
  }

  _unfocus() {
    this.unfocusState()
  }

  focusState() {
    this.patch({
      Button: {
        color: getColor(grey50),
        Label: { visible: true },
        Image: {
          color: getColor(grey960),
          flexItem: { marginTop: this._offsetY, marginLeft: this._offsetX },
        },
      },
      Label: { visible: true },
    })
  }

  unfocusState() {
    this.patch({
      Button: {
        color: getColor(grey700),
        Label: { visible: false },
        Image: {
          color: getColor(grey50),
          flexItem: { marginTop: this._offsetY, marginLeft: this._offsetX },
        },
      },
      Label: { visible: false },
    })
  }

  _handleHover() {
    this.fireAncestors('$updateFocusByHover', this)
    this._focus()
  }

  _handleUnhover() {
    this._unfocus()
  }

  _handleClick() {
    // Must bubble up so parent container can execute _handleEnter()
    return false
  }
}

class PlayPauseButton extends PlayerButton {
  private _isPlaying: boolean
  private _playSrc: string
  private _pauseSrc: string
  private _playLabel: string
  private _pauseLabel: string

  _construct() {
    this._isPlaying = false
  }

  set images({ play, pause }) {
    this._playSrc = play
    this._pauseSrc = pause
    this._updateMedia()
  }

  set labels({ play, pause }) {
    this._playLabel = play
    this._pauseLabel = pause
    this._updateMedia()
  }

  set isPlaying(value) {
    this._isPlaying = value
    this._updateMedia()
  }

  get isPlaying() {
    return this._isPlaying
  }

  get speechText(): string {
    return this.isPlaying ? this._pauseLabel + 'button' : this._playLabel + 'button'
  }

  private _updateMedia() {
    this.offsetX = this.isPlaying ? 0 : 4
    this.image = this._isPlaying ? this._pauseSrc : this._playSrc
    this.label = this._isPlaying ? this._pauseLabel : this._playLabel
  }
}

class GenericPlayerButton extends PlayerButton {
  private _isFocused: boolean
  private _imgSrc: string
  private _focusLabel = ''
  private _unfocusLabel = ''

  _construct() {
    this._isFocused = false
  }

  _init() {
    this.unfocusState()
  }

  set images({ main }) {
    this._imgSrc = main
    this._updateMedia()
  }

  set labels({ focused, unfocused = '' }) {
    this._focusLabel = focused
    this._unfocusLabel = unfocused
    this._updateMedia()
  }

  get speechText(): string {
    return this.isFocused ? this._focusLabel + 'button' : this._unfocusLabel + 'button'
  }

  set isFocused(value) {
    this._isFocused = value
    this._updateMedia()
  }

  get isFocused() {
    return this._isFocused
  }

  private _updateMedia() {
    this.image = this._imgSrc
    this.label = this._isFocused ? this._unfocusLabel : this._focusLabel
  }
}

export { PlayerButton, PlayPauseButton, GenericPlayerButton }
